import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff, Pin } from "lucide-react";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "visible", label: "Visible" },
  { key: "pinned", label: "Featured", testidKey: "featured" },
  { key: "hidden", label: "Hidden" },
];

const STATUS_PILL = {
  visible: ["VISIBLE", "border-[#5fc377]/40 text-[#5fc377]"],
  pinned: ["FEATURED", "border-[#C9A055]/40 text-[#C9A055]"],
  hidden: ["HIDDEN", "border-[#d97a7a]/40 text-[#d97a7a]"],
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await http.get(`/admin/reviews${q}`);
      setReviews(data);
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (rid, status) => {
    try {
      await http.patch(`/admin/reviews/${rid}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete this review permanently? This cannot be undone.")) return;
    try {
      await http.delete(`/admin/reviews/${rid}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <div className="font-display text-2xl text-[#F4F1EA]">Reviews & Ratings</div>
          <div className="font-body text-[13px] text-[#8a857d] mt-1">
            Moderate customer ratings and comments. Feature great ones, hide spam, delete anything off-brand. {reviews.length} {filter === "all" ? "total" : filter}.
          </div>
        </div>
        <div className="flex gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              data-testid={`reviews-filter-${s.testidKey || s.key}`}
              className={`px-3 py-1.5 border font-ui text-[10px] tracking-[0.22em] uppercase ${filter === s.key ? "border-[#C9A055] text-[#C9A055]" : "border-white/15 text-[#9a9690] hover:text-[#F4F1EA]"}`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-12 text-center font-body text-[#8a857d]">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-12 text-center font-body text-[#8a857d]">No reviews{filter !== "all" ? ` with status "${filter}"` : " yet"}.</div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => {
            const [pillLabel, pillCls] = STATUS_PILL[r.status] || ["?", "border-white/15 text-[#9a9690]"];
            return (
              <div
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className={`border bg-[#0a0a0a] p-5 ${r.status === "pinned" ? "border-[#C9A055]/30" : "border-white/10"}`}
              >
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-2 py-0.5 border font-ui text-[9px] tracking-[0.22em] ${pillCls}`}>{pillLabel}</span>
                      <span className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">FOR</span>
                      <span className="font-ui text-[11px] tracking-[0.18em] text-[#C9A055] uppercase">{r.artwork_title}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="font-ui text-[12px] tracking-[0.18em] text-[#F4F1EA] uppercase">{r.name || "Anonymous"}</div>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            key={i}
                            size={12}
                            className={i <= r.stars ? "fill-[#D4A547] text-[#D4A547]" : "text-[#5a5650]"}
                          />
                        ))}
                      </div>
                      <span className="font-ui-tight text-[10px] text-[#8a857d]">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    {r.comment && (
                      <p className="font-body text-[14px] text-[#B5B0A7] mt-3 leading-relaxed">
                        "{r.comment}"
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {r.status !== "pinned" && (
                      <button onClick={() => updateStatus(r.id, "pinned")} data-testid={`review-pin-${r.id}`} title="Feature" className="p-2 border border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055] hover:text-black"><Pin size={13}/></button>
                    )}
                    {r.status === "pinned" && (
                      <button onClick={() => updateStatus(r.id, "visible")} title="Un-feature" className="p-2 border border-[#C9A055] text-black bg-[#C9A055] hover:opacity-90"><Pin size={13}/></button>
                    )}
                    {r.status === "hidden" ? (
                      <button onClick={() => updateStatus(r.id, "visible")} data-testid={`review-show-${r.id}`} title="Show" className="p-2 border border-[#5fc377]/40 text-[#5fc377] hover:bg-[#5fc377] hover:text-black"><Eye size={13}/></button>
                    ) : (
                      <button onClick={() => updateStatus(r.id, "hidden")} data-testid={`review-hide-${r.id}`} title="Hide" className="p-2 border border-white/15 text-[#9a9690] hover:text-[#d97a7a] hover:border-[#d97a7a]"><EyeOff size={13}/></button>
                    )}
                    <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} title="Delete" className="p-2 border border-white/15 text-[#9a9690] hover:text-[#d97a7a] hover:border-[#d97a7a]"><Trash2 size={13}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
